import * as THREE from 'three';

// One distinct colour per seat so each player's pointer is identifiable.
const SEAT_COLORS = [0xff5a5a, 0x5a9bff, 0x5ad46b, 0xffd24a];
const HOVER = 0.05; // how far the arrow tip floats above the cloth (m)
const CONE_H = 0.11;
const CONE_R = 0.03;

interface Ptr {
  group: THREE.Group;
  cone: THREE.Mesh;
  x: number;
  y: number;
}

/**
 * Floating 3D "ping" arrows other players drop on the table. Each seat gets its
 * own colour and one pointer; the arrow hovers (bobbing) above the pointed spot
 * with a ring marking the exact location on the cloth. Z-up world (cloth at z=0).
 */
export class TablePointers {
  readonly group = new THREE.Group();
  private readonly ptrs = new Map<number, Ptr>();

  private make(seat: number): Ptr {
    const color = SEAT_COLORS[((seat % SEAT_COLORS.length) + SEAT_COLORS.length) % SEAT_COLORS.length];
    const g = new THREE.Group();
    const cone = new THREE.Mesh(
      new THREE.ConeGeometry(CONE_R, CONE_H, 20),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.95, depthTest: false })
    );
    cone.rotation.x = -Math.PI / 2; // apex points down (-Z), toward the table
    cone.renderOrder = 999; // draw over the balls/cloth so it's always visible
    g.add(cone);
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.02, 0.032, 24),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.5, side: THREE.DoubleSide, depthTest: false })
    );
    ring.position.z = 0.003; // sit just on the cloth
    ring.renderOrder = 998;
    g.add(ring);
    return { group: g, cone, x: 0, y: 0 };
  }

  /** Show or move seat's pointer to table position (x, y). */
  set(seat: number, x: number, y: number): void {
    let p = this.ptrs.get(seat);
    if (!p) {
      p = this.make(seat);
      this.group.add(p.group);
      this.ptrs.set(seat, p);
    }
    p.x = x;
    p.y = y;
  }

  /** Remove seat's pointer. */
  clear(seat: number): void {
    const p = this.ptrs.get(seat);
    if (!p) return;
    this.group.remove(p.group);
    p.group.traverse((o) => {
      const m = o as THREE.Mesh;
      m.geometry?.dispose?.();
      (m.material as THREE.Material)?.dispose?.();
    });
    this.ptrs.delete(seat);
  }

  clearAll(): void {
    for (const seat of [...this.ptrs.keys()]) this.clear(seat);
  }

  /** How many pointers are currently shown (0 = idle, skip rendering). */
  get count(): number {
    return this.ptrs.size;
  }

  /** Animate the hover bob. `t` = ms timestamp. */
  update(t: number): void {
    for (const [seat, p] of this.ptrs) {
      p.group.position.set(p.x, p.y, 0);
      const bob = 0.02 * Math.sin(t * 0.004 + seat);
      p.cone.position.z = HOVER + CONE_H / 2 + bob; // apex floats ~HOVER above the cloth
    }
  }
}
